import React, { useCallback, useContext, useMemo, useRef } from "react";
import { Button, IconButton, useTheme } from "@mui/material";
import { DescriptionRounded } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
    ColDef, ColumnMovedEvent, ColumnVisibleEvent, GridReadyEvent, ICellRendererParams,
    IServerSideDatasource, SideBarDef
} from "ag-grid-community";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { GRID_LIST_PAGE_SIZE } from "../../config";
import { raFilterParams, raSearchListState } from "../state";
import { raSearchList } from "../services/http.services";
import { EXPIRED_DEFAULT_COL_DEF, EXPIRED_SIDE_BAR, getExpiredColumnDefs, getGeneralColumnDefs } from "../config";
import { deepClone } from "../../../../util/state/commonFunctions";
import { useHasScope } from "../../../../config/globalConfig";
import { NetworkScope } from "../../types";
import CustomLoader from "../../../../util/component/CustomLoader";
import { setSnackbar } from "../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../util/state/globalVariables";
import { AgGridCustomToolbarProps } from "../../../../util/component/AgGridCustomToolbar";
import ToolbarActions from "../ra-results-list-toolbar/ToolbarAction";

const ResultsList = ({ type }: { type: "general" | "expired" }) => {
    const hasScope = useHasScope;
    const theme = useTheme();
    const navigate = useNavigate();
    const { t } = useTranslation(['network', 'translation']);
    const { globalData } = useContext(GlobalContext);
    const { dispatchGlobalData } = useContext(GlobalContext);

    const raListRef = useRef<any[]>([]);
    const setRaList = useSetRecoilState(raSearchListState);

    const filterParams = useRecoilValue(raFilterParams);

    /** Table action */
    const handleTableAction = (params: ICellRendererParams<any>, option?: string) => {
        switch (type) {
            case 'general':
                navigate(`/network/ra-detail/${params.data.idRentalAgreement}`);
                break;
            case 'expired':
                switch (option) {
                    case "addExtension":
                        navigate(`/network/extension-management/${params.data.idRentalAgreement}`);
                        break;
                    case "ownershipFinance":
                        navigate(`/network`);
                        break;
                }
                break;
        }
    }

    const defaultColDef = useMemo<ColDef>(() => EXPIRED_DEFAULT_COL_DEF, []);

    const sideBar = useMemo<SideBarDef>(() => EXPIRED_SIDE_BAR, []);

    const columnDefs = useMemo<ColDef[]>(() => {
        let columnDefs;
        switch (type) {
            case "general":
                columnDefs = getGeneralColumnDefs(t);
                columnDefs.splice(0, 0, {
                    field: '_actions',
                    headerName: '',
                    minWidth: 70,
                    resizable: false,
                    suppressMovable: true,
                    suppressSizeToFit: false,
                    suppressColumnsToolPanel: true,
                    pinned: 'right',
                    cellRenderer: (params: ICellRendererParams<any>) => {
                        return (
                            hasScope(NetworkScope.RaDetail) &&
                            <Link to={`/network/ra-detail/${params.data.idRentalAgreement}`} target="_blank">
                                <IconButton title={t('label.details')} color="accent">
                                    <DescriptionRounded />
                                </IconButton>
                            </Link>
                        );
                    }
                });
                break;
            case "expired":
                columnDefs = getExpiredColumnDefs(t);
                columnDefs.splice(0, 0, {
                    field: '_actions',
                    headerName: '',
                    minWidth: 210,
                    resizable: false,
                    suppressMovable: true,
                    suppressColumnsToolPanel: true,
                    pinned: 'right',
                    cellRenderer: (params: ICellRendererParams<any>) => {
                        return (
                            <>
                                {
                                    hasScope(NetworkScope.RaInsert) &&
                                    <Button variant={"text"} color={"accent"} onClick={() => handleTableAction(params, 'addExtension')}>
                                        {t('raSearch.button.addExtension')}
                                    </Button>
                                }

                                {
                                    hasScope(NetworkScope.RaDetail) &&
                                    <IconButton title={t('label.details')}
                                        color="accent"
                                        onClick={() => handleTableAction(params, 'ownershipFinance')}
                                        sx={{ mx: theme.spacing(0.5) }}>
                                        <DescriptionRounded />
                                    </IconButton>
                                }
                            </>
                        );
                    }
                });
                break;
        }

        return columnDefs;
    }, [t]);

    const columnStateStorageKey = useMemo(() => {
        return `__NETWORK_PENDING.tableColumsConfig:${globalData.user.username}`;
    }, [globalData.user.username]);

    const onGridReady = useCallback((event: GridReadyEvent) => {
        const colState = JSON.parse(localStorage.getItem(columnStateStorageKey) || '[]');
        event.columnApi.applyColumnState({
            state: colState,
            applyOrder: true
        });

        event.api.sizeColumnsToFit();
    }, [columnStateStorageKey]);

    const onColumnVisible = useCallback((event: ColumnVisibleEvent) => {
        localStorage.setItem(columnStateStorageKey, JSON.stringify(event.columnApi.getColumnState()));
    }, [columnStateStorageKey]);

    const onColumnMoved = useCallback((event: ColumnMovedEvent) => {
        localStorage.setItem(columnStateStorageKey, JSON.stringify(event.columnApi.getColumnState()));
    }, [columnStateStorageKey]);

    /** Create Form */
    const raSearchListMutation = useMutation(raSearchList);

    const serverSideDatasource = useMemo<IServerSideDatasource>(() => ({
        getRows: async (params) => {
            if (filterParams == null) {
                params.success({ rowCount: 0, rowData: [] });
                params.api.showNoRowsOverlay();
                raListRef.current = []
                return;
            }

            let filterParamsClone = deepClone(filterParams);

            const sortBy =
                params.request.sortModel[0] ?
                    {
                        field: params.request.sortModel[0].colId,
                        order: params.request.sortModel[0].sort
                    } :
                    undefined;

            const limit =
                (params.request.endRow !== undefined && params.request.startRow !== undefined) ?
                    (params.request.endRow - params.request.startRow) :
                    undefined;

            if (!filterParamsClone) {
                filterParamsClone = {};
            }

            if (!!filterParamsClone.tenants) {
                const ind = filterParamsClone.tenants.findIndex((elem: any) => elem.name === 'all');
                if (ind > -1) {
                    filterParamsClone.tenants =
                        globalData.user.legalEntities
                            .filter((elem: any) => elem.name !== 'all')
                            .map((elem: any) => elem.name);
                } else {
                    filterParamsClone.tenants = filterParamsClone.tenants.map((elem: any) => elem.name);
                }
            }
            filterParamsClone.dealers = !!filterParamsClone.dealers ? filterParamsClone.dealers.map((elem: any) => elem.name) : null;

            filterParamsClone['paramsRequest'] = {
                // num elem per pagina
                pagination: limit,
                // primo elemento della paginazione
                startIndex: params.request.startRow,
                endIndex: params.request.endRow
            }

            if (!!sortBy) {
                filterParamsClone.paramsRequest.sortBy = sortBy;
            }

            raSearchListMutation.mutateAsync(filterParamsClone)
                .then((response: any) => {
                    raListRef.current = response.searchRAResponses ?? [];   

                    params.success({
                        rowData: response.searchRAResponses,
                        rowCount: response.searchParamsResponse.totCount
                    });

                    if (response.searchParamsResponse.totCount === 0) {
                        params.api.showNoRowsOverlay();
                    }
                })
                .catch((err: AxiosError) => {
                    params.fail();

                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t('translation:error.tryLater'),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true
                            }
                        }
                    });
                });
        }
    }), [filterParams])

    const toolbar = useMemo<AgGridCustomToolbarProps>(() => ({
        renderActions: (params) => <ToolbarActions {...params} raListRef={raListRef} />,
    }), []);

    return (
        <>
            <AgGridCustomTable
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                onColumnVisible={onColumnVisible}
                onColumnMoved={onColumnMoved}
                onGridReady={onGridReady}
                serverSideDatasource={serverSideDatasource}
                rowModelType="serverSide"
                serverSideInfiniteScroll
                paginationPageSize={GRID_LIST_PAGE_SIZE}
                cacheBlockSize={GRID_LIST_PAGE_SIZE}
                sideBar={sideBar}
                toolbar={toolbar}
                suppressDragLeaveHidesColumns
                suppressMultiSort
                enableCellTextSelection
            />

            <CustomLoader loading={raSearchListMutation.isLoading} />
        </>
    );
}

export default ResultsList;